import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Row,
  Col,
  DatePicker,
  message,
  Spin,
  Divider,
  Alert,
  Tag,
  Statistic,
  Space,
  Descriptions
} from 'antd';
import {
  UserOutlined,
  CarOutlined,
  BarcodeOutlined,
  EuroOutlined,
  NumberOutlined,
  FileTextOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  SaveOutlined,
  CalculatorOutlined,
  PlusOutlined
} from '@ant-design/icons';
import axios from '../../../utils/axios';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;

const AddVentes = ({ visible, onCancel, onSuccess, vente, actionType }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [chauffeurs, setChauffeurs] = useState([]);
  const [calculations, setCalculations] = useState({
    sousTotal: 0,
    montantTotal: 0,
  });

  const isViewMode = actionType === 'view';
  const isEditMode = actionType === 'edit';

  useEffect(() => {
    if (visible) {
      fetchClients();
      fetchChauffeurs();
     
      if (vente) {
        // Formater la vente pour l'affichage/édition
        const formattedVente = {
          ...vente,
          date: vente.dateFacture ? dayjs(vente.dateFacture) : dayjs(),
          client: vente.clientId || vente.client, // Pour compatibilité
          chauffeur: vente.chauffeurId || vente.chauffeur, // Pour compatibilité
          statut: vente.statut || 'En attente'
        };
        form.setFieldsValue(formattedVente);
       
        // Calculer les montants
        if (vente.quantite && vente.prixUnitaire) {
          const montant = vente.quantite * vente.prixUnitaire;
          setCalculations({
            sousTotal: montant,
            montantTotal: montant,
          });
        }
      } else {
        form.resetFields();
        setCalculations({
          sousTotal: 0,
          montantTotal: 0,
        });
       
        // Générer un numéro de facture par défaut
        form.setFieldsValue({
          numeroFacture: `FACT-${dayjs().format('YYMMDD')}-${Math.floor(Math.random() * 1000)}`,
          date: dayjs(),
          modePaiement: 'Espèces',
          statut: 'En attente',
        });
      }
    }
  }, [visible, vente, form]);

  const fetchClients = async () => {
    try {
      const response = await axios.get('/api/clients', {
        params: { limit: 1000 }
      });
      if (response.data.success) {
        setClients(response.data.data.clients || []);
      }
    } catch (error) {
      console.error('❌ Erreur chargement clients:', error);
      message.error('Erreur lors du chargement des clients');
    }
  };

  const fetchChauffeurs = async () => {
    try {
      const response = await axios.get('/api/chauffeurs', {
        params: { limit: 1000 }
      });
      if (response.data.success) {
        setChauffeurs(response.data.data.chauffeurs || []);
      }
    } catch (error) {
      console.error('❌ Erreur chargement chauffeurs:', error);
      message.error('Erreur lors du chargement des chauffeurs');
    }
  };

  const calculateMontant = (quantite, prixUnitaire) => {
    const qte = parseFloat(quantite) || 0;
    const prix = parseFloat(prixUnitaire) || 0;
    const montant = qte * prix;
   
    setCalculations({
      sousTotal: montant,
      montantTotal: montant,
    });
  };

  const handleValuesChange = (changedValues, allValues) => {
    // Recalculer le montant quand quantité ou prix unitaire change
    if (changedValues.quantite || changedValues.prixUnitaire) {
      const { quantite, prixUnitaire } = allValues;
      calculateMontant(quantite, prixUnitaire);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // 1. Validation du formulaire
      const values = await form.validateFields();

      // 2. Récupérer le client sélectionné
      const selectedClient = clients.find((c) => c._id === values.client);
      if (!selectedClient) {
        throw new Error('Client invalide');
      }

      // 3. Récupérer le chauffeur sélectionné (optionnel)
      const selectedChauffeur = chauffeurs.find((c) => c._id === values.chauffeur);

      // 4. Préparation des données pour le backend
      const venteData = {
        // Informations principales
        designation: values.designation,
        clientId: selectedClient._id,
        
        // Informations de facturation
        numeroFacture: values.numeroFacture,
        dateFacture: values.date ? values.date.toDate() : new Date(),
        
        // Montants
        quantite: Number(values.quantite),
        prixUnitaire: Number(values.prixUnitaire),
        montant: calculations.montantTotal,
        
        // Autres informations
        modePaiement: values.modePaiement,
        statut: isEditMode ? values.statut : 'En attente',
        note: values.note || '',
      };

      // Ajouter chauffeurId si sélectionné
      if (selectedChauffeur) {
        venteData.chauffeurId = selectedChauffeur._id;
      }

      console.log('📤 Données envoyées au backend:', venteData);

      // 5. Appel API
      let response;
      if (isEditMode && vente?.id) {
        response = await axios.put(`/api/ventes/${vente.id}`, venteData);
        message.success('Vente modifiée avec succès');
      } else {
        response = await axios.post('/api/ventes', venteData);
        message.success('Vente créée avec succès');
      }

      // 6. Gestion du résultat
      if (response.data?.success) {
        onSuccess();
      } else {
        throw new Error(response.data?.message || 'Erreur lors de la sauvegarde');
      }

    } catch (error) {
      console.error('❌ Erreur soumission vente:', error);
      console.error('📛 Réponse backend:', error.response?.data);
      
      let errorMessage = 'Erreur lors de la sauvegarde';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderViewMode = () => (
    <Descriptions
      bordered
      column={1}
      size="middle"
      className="mb-4"
    >
      <Descriptions.Item label="N° Facture">
        <Tag color="blue" className="font-bold">
          {vente?.numeroFacture}
        </Tag>
      </Descriptions.Item>
     
      <Descriptions.Item label="Désignation">
        {vente?.designation}
      </Descriptions.Item>
     
      <Descriptions.Item label="Client">
        {vente?.client}
      </Descriptions.Item>
     
      <Descriptions.Item label="Chauffeur">
        {vente?.chauffeur || 'Non spécifié'}
      </Descriptions.Item>
     
      <Descriptions.Item label="Quantité">
        <span className="font-bold">{vente?.quantite} tonnes</span>
      </Descriptions.Item>
     
      <Descriptions.Item label="Prix Unitaire">
        <span className="font-bold">
          {Number(vente?.prixUnitaire).toLocaleString('fr-FR', {
            style: 'currency',
            currency: 'XOF',
            minimumFractionDigits: 0,
          })}
        </span>
      </Descriptions.Item>
     
      <Descriptions.Item label="Montant Total">
        <span className="font-bold text-green-600 text-lg">
          {Number(vente?.montant).toLocaleString('fr-FR', {
            style: 'currency',
            currency: 'XOF',
            minimumFractionDigits: 0,
          })}
        </span>
      </Descriptions.Item>
     
      <Descriptions.Item label="Mode de Paiement">
        <Tag color="purple">{vente?.modePaiement}</Tag>
      </Descriptions.Item>
     
      <Descriptions.Item label="Statut">
        <Tag
          color={
            vente?.statut === 'Validée' ? 'success' :
            vente?.statut === 'Payée' ? 'processing' :
            vente?.statut === 'En attente' ? 'warning' :
            vente?.statut === 'Annulée' ? 'error' : 'default'
          }
        >
          {vente?.statut}
        </Tag>
      </Descriptions.Item>
     
      <Descriptions.Item label="Date">
        {vente?.dateFacture ? dayjs(vente.dateFacture).format('DD/MM/YYYY') : 
         vente?.date ? dayjs(vente.date).format('DD/MM/YYYY') : 'N/A'}
      </Descriptions.Item>
     
      <Descriptions.Item label="Note">
        {vente?.note || 'Aucune note'}
      </Descriptions.Item>
    </Descriptions>
  );

  const renderFormMode = () => (
    <Form
      form={form}
      layout="vertical"
      onValuesChange={handleValuesChange}
      className="mt-4"
    >
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Form.Item
            label="N° Facture"
            name="numeroFacture"
            rules={[
              { required: true, message: 'Veuillez saisir le numéro de facture' },
              { pattern: /^[A-Za-z0-9-]+$/, message: 'Format invalide' }
            ]}
          >
            <Input
              prefix={<BarcodeOutlined />}
              placeholder="Ex: FACT-20231201-001"
              size="large"
              disabled={isViewMode}
              allowClear
            />
          </Form.Item>
        </Col>
       
        <Col xs={24} md={12}>
          <Form.Item
            label="Date"
            name="date"
            rules={[{ required: true, message: 'Veuillez sélectionner la date' }]}
          >
            <DatePicker
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
              size="large"
              placeholder="Sélectionnez la date"
              disabled={isViewMode}
            />
          </Form.Item>
        </Col>
       
        <Col xs={24}>
          <Form.Item
            label="Désignation"
            name="designation"
            rules={[
              { required: true, message: 'Veuillez saisir la désignation' },
              { min: 3, message: 'Minimum 3 caractères' }
            ]}
          >
            <Input
              prefix={<FileTextOutlined />}
              placeholder="Ex: Transport de marchandises"
              size="large"
              disabled={isViewMode}
              allowClear
            />
          </Form.Item>
        </Col>
       
        <Col xs={24} md={12}>
          <Form.Item
            label="Client"
            name="client"
            rules={[{ required: true, message: 'Veuillez sélectionner le client' }]}
          >
            <Select
              showSearch
              placeholder="Sélectionnez un client"
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
              size="large"
              disabled={isViewMode}
              allowClear
            >
              <Option value="">Sélectionner un client</Option>
              {clients.map((client) => (
                <Option key={client._id} value={client._id}>
                  {client.nom} {client.prenom ? `${client.prenom}` : ''}
                  {client.telephone ? ` - ${client.telephone}` : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
       
        <Col xs={24} md={12}>
          <Form.Item
            label="Chauffeur"
            name="chauffeur"
          >
            <Select
              showSearch
              placeholder="Sélectionnez un chauffeur (optionnel)"
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
              size="large"
              disabled={isViewMode}
              allowClear
            >
              <Option value="">Sélectionner un chauffeur (optionnel)</Option>
              {chauffeurs.map((chauffeur) => (
                <Option key={chauffeur._id} value={chauffeur._id}>
                  {chauffeur.nom} {chauffeur.prenom ? `${chauffeur.prenom}` : ''}
                  {chauffeur.telephone ? ` - ${chauffeur.telephone}` : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
       
        <Col xs={12} md={8}>
          <Form.Item
            label="Quantité (tonnes)"
            name="quantite"
            rules={[
              { required: true, message: 'Veuillez saisir la quantité' },
              { type: 'number', min: 0.1, message: 'La quantité doit être supérieure à 0' },
            ]}
            help="Saisissez la quantité en tonnes"
          >
            <InputNumber
              min={0.1}
              step={0.1}
              style={{ width: '100%' }}
              placeholder="0.00"
              size="large"
              prefix={<NumberOutlined />}
              disabled={isViewMode}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
              parser={(value) => value.replace(/\s/g, '')}
            />
          </Form.Item>
        </Col>
       
        <Col xs={12} md={8}>
          <Form.Item
            label="Prix Unitaire (FCFA/tonne)"
            name="prixUnitaire"
            rules={[
              { required: true, message: 'Veuillez saisir le prix unitaire' },
              { type: 'number', min: 1, message: 'Le prix doit être positif' },
            ]}
            help="Prix par tonne"
          >
            <InputNumber
              min={1}
              style={{ width: '100%' }}
              placeholder="0"
              size="large"
              prefix={<EuroOutlined />}
              disabled={isViewMode}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
              parser={(value) => value.replace(/\s/g, '')}
            />
          </Form.Item>
        </Col>
       
        <Col xs={24} md={8}>
          <Form.Item label="Montant Total (FCFA)">
            <div className="text-center p-3 bg-gray-50 rounded-lg border">
              <div className="text-2xl font-bold text-green-600">
                {calculations.montantTotal.toLocaleString('fr-FR', {
                  style: 'currency',
                  currency: 'XOF',
                  minimumFractionDigits: 0,
                })}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                Calcul automatique
              </div>
            </div>
          </Form.Item>
        </Col>
       
        <Col xs={12} md={8}>
          <Form.Item
            label="Mode de Paiement"
            name="modePaiement"
            rules={[{ required: true, message: 'Veuillez sélectionner le mode de paiement' }]}
          >
            <Select
              placeholder="Sélectionnez le mode"
              size="large"
              disabled={isViewMode}
              allowClear
            >
              <Option value="Espèces">Espèces</Option>
              <Option value="Virement">Virement</Option>
              <Option value="Chèque">Chèque</Option>
              <Option value="Carte Bancaire">Carte Bancaire</Option>
              <Option value="Crédit">Crédit</Option>
            </Select>
          </Form.Item>
        </Col>
       
        <Col xs={12} md={8}>
          <Form.Item
            label="Statut"
            name="statut"
            rules={[{ required: true, message: 'Veuillez sélectionner le statut' }]}
          >
            <Select
              placeholder="Sélectionnez le statut"
              size="large"
              disabled={isViewMode || !isEditMode}
              allowClear
            >
              <Option value="En attente">En attente</Option>
              {isEditMode && (
                <>
                  <Option value="Validée">Validée</Option>
                  <Option value="Payée">Payée</Option>
                  <Option value="Annulée">Annulée</Option>
                </>
              )}
            </Select>
          </Form.Item>
          {!isEditMode && (
            <div className="text-sm text-yellow-600 mt-1">
              Le statut sera défini automatiquement à "En attente" pour les nouvelles ventes
            </div>
          )}
        </Col>
       
        <Col xs={24}>
          <Form.Item
            label="Note"
            name="note"
          >
            <TextArea
              rows={3}
              placeholder="Note supplémentaire..."
              disabled={isViewMode}
              maxLength={500}
              showCount
              allowClear
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );

  const getModalTitle = () => {
    switch (actionType) {
      case 'add':
        return 'Nouvelle Vente';
      case 'edit':
        return 'Modifier la Vente';
      case 'view':
        return 'Détails de la Vente';
      default:
        return 'Vente';
    }
  };

  const getModalWidth = () => {
    return isViewMode ? 800 : 900;
  };

  return (
    <Modal
      title={
        <div className="flex items-center">
          {isViewMode ? <EyeOutlined /> :
           isEditMode ? <CheckCircleOutlined /> :
           <PlusOutlined />}
          <span className="ml-2 text-lg font-semibold">{getModalTitle()}</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={getModalWidth()}
      footer={
        isViewMode ? (
          <Button type="primary" onClick={onCancel}>
            Fermer
          </Button>
        ) : (
          <Space>
            <Button onClick={onCancel} disabled={loading}>
              <CloseCircleOutlined /> Annuler
            </Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={loading}
              icon={<SaveOutlined />}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isEditMode ? 'Modifier' : 'Créer'}
            </Button>
          </Space>
        )
      }
      className="max-h-[90vh] overflow-y-auto"
      destroyOnClose
    >
      <Spin spinning={loading} tip="Traitement en cours...">
        {isViewMode ? renderViewMode() : renderFormMode()}
       
        {/* Résumé pour mode formulaire */}
        {!isViewMode && (
          <>
            <Divider />
            <Alert
              message="Résumé du calcul"
              description={
                <Row gutter={[16, 16]} className="mt-2">
                  <Col span={12}>
                    <Statistic
                      title="Sous-total"
                      value={calculations.sousTotal}
                      prefix="FCFA"
                      valueStyle={{ fontSize: '16px' }}
                      formatter={(value) => Number(value).toLocaleString('fr-FR')}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Montant Total"
                      value={calculations.montantTotal}
                      prefix="FCFA"
                      valueStyle={{ fontSize: '18px', color: '#3f8600' }}
                      formatter={(value) => Number(value).toLocaleString('fr-FR')}
                    />
                  </Col>
                </Row>
              }
              type="info"
              showIcon
              icon={<CalculatorOutlined />}
            />
          </>
        )}
      </Spin>
    </Modal>
  );
};

export default AddVentes;